import { useFormikContext } from 'formik'
import {
  ButtonIcon,
  Flex,
  FormControl,
  FormLabel,
  Link,
  Switch,
  Tooltip
} from 'front-end/src/components'
import IconCopy from 'front-end/src/components/common/icon/IconCopy'
import IconQuestion from 'front-end/src/components/common/icon/IconQuestion'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import React from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { brandColors } from 'shared/basicPalette'
import { v4 as uuidv4 } from 'uuid'

import { t, Trans } from '@lingui/macro'
import { FormLabelIcon } from '../../../components/event-form/EventFormLabelIcon'
import { truncateMSTeamsUrl } from 'front-end/src/utils/url'
import { useManageEventContext } from 'front-end/src/pages/manage-events/ManageEventContext'
import { ManageEventDetailDayjs } from 'front-end/src/pages/manage-events/event-edit/EventEditPage'

type EventDetailCalendarIntegrationProps = {
  hasGoogleIntegration: boolean
  hasMSIntegration: boolean
}

export const EventDetailCalendarIntegration = ({
  hasGoogleIntegration,
  hasMSIntegration
}: EventDetailCalendarIntegrationProps) => {
  const { event } = useManageEventContext()
  const { values, setFieldValue } = useFormikContext<ManageEventDetailDayjs>()

  return (
    <>
      {hasGoogleIntegration && (
        <Flex mb="16px">
          <Flex>
            <FormLabelIcon icon={null} />
          </Flex>
          <Flex width="calc(100% - 34px)">
            <FormControl
              display="flex"
              alignItems="center"
              flexWrap="nowrap"
              width="40%"
            >
              <FormLabel
                htmlFor="google-meet"
                id="google-meet-label"
                fontSize="15px"
                color={brandColors.grey[700]}
                my="0"
                whiteSpace="nowrap"
              >
                <Trans>Enable Google Meet</Trans>
              </FormLabel>
              <Tooltip
                text={t`When enabled and published, Google Meet conference will be will be created for this event.`}
              >
                <IconQuestion color={brandColors.grey.mid} display="block" />
              </Tooltip>
              <Switch
                id="google-meet"
                ml="16px"
                isChecked={!!values.google_meet_uuid}
                onChange={(e) => {
                  const isChecked = e.currentTarget.checked
                  setFieldValue(
                    'google_meet_uuid',
                    isChecked ? values.google_meet_uuid || uuidv4() : null
                  )
                }}
              />
            </FormControl>
            {event.published_date && event.google_meet_id && (
              <FormControl
                pl="16px"
                display="flex"
                alignItems="center"
                flexWrap="nowrap"
                minH="46px"
                width="60%"
              >
                <Link
                  href={`https://meet.google.com/${event.google_meet_id}`}
                  isExternal
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >{`meet.google.com/${event.google_meet_id}`}</Link>
                <CopyToClipboard
                  text={`https://meet.google.com/${event.google_meet_id}`}
                  onCopy={() => {
                    notification.info(t`Google meet was copied to clipboard`)
                  }}
                >
                  <ButtonIcon
                    circular
                    ml="15px"
                    aria-label={`Copy google meet link`}
                  >
                    <IconCopy color={brandColors.grey.mid} boxSize="18px" />
                  </ButtonIcon>
                </CopyToClipboard>
              </FormControl>
            )}
          </Flex>
        </Flex>
      )}

      {/* Only for regular events */}
      {hasMSIntegration &&
        !event?.recurrence?.freq &&
        !values?.recurrence?.freq && (
          <Flex mb="16px">
            <Flex>
              <FormLabelIcon icon={null} />
            </Flex>
            <Flex width="calc(100% - 34px)">
              <FormControl
                display="flex"
                alignItems="center"
                flexWrap="nowrap"
                width="51%"
              >
                <FormLabel
                  htmlFor="ms-teams"
                  id="ms-teams-label"
                  fontSize="15px"
                  color={brandColors.grey[700]}
                  my="0"
                  whiteSpace="nowrap"
                >
                  <Trans>Enable Microsoft Teams meeting</Trans>
                </FormLabel>
                <Tooltip
                  text={t`When enabled and published, Microsoft Teams conference will be will be created for this event.`}
                >
                  <IconQuestion color={brandColors.grey.mid} display="block" />
                </Tooltip>
                <Switch
                  id="ms-teams"
                  ml="16px"
                  isChecked={!!values?.ms_teams_enabled}
                  onChange={(e) => {
                    const isChecked = e.currentTarget.checked
                    setFieldValue('ms_teams_enabled', isChecked)
                  }}
                />
              </FormControl>
              {event.published_date &&
                event.ms_teams_enabled &&
                event.ms_teams_url && (
                  <FormControl
                    pl="16px"
                    display="flex"
                    alignItems="center"
                    flexWrap="nowrap"
                    minH="46px"
                    width="60%"
                  >
                    <Link
                      href={event.ms_teams_url}
                      isExternal
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {truncateMSTeamsUrl(event.ms_teams_url)}
                    </Link>
                    <CopyToClipboard
                      text={event.ms_teams_url}
                      onCopy={() => {
                        notification.info(
                          t`MS Teams link was copied to clipboard`
                        )
                      }}
                    >
                      <ButtonIcon
                        circular
                        ml="15px"
                        aria-label={t`Copy MS teams meet link`}
                      >
                        <IconCopy color={brandColors.grey.mid} boxSize="18px" />
                      </ButtonIcon>
                    </CopyToClipboard>
                  </FormControl>
                )}
            </Flex>
          </Flex>
        )}
    </>
  )
}
